
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Map, 
  Image, 
  Settings, 
  Users,
  Info
} from 'lucide-react';
import { useIsMobile } from "@/hooks/use-mobile";
import { DesktopSidebar } from './layout/DesktopSidebar';
import { MobileSidebar } from './layout/MobileSidebar';
import { NavItemType } from './layout/types';
import { Outlet } from 'react-router-dom';

export default function CMSLayout() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const renderCountRef = useRef(0);
  const mountTimeRef = useRef(Date.now());
  
  // REMOVED ALL VISIBILITY CHANGE AND EVENT MONITORING
  // Only basic render counting for debugging
  useEffect(() => {
    renderCountRef.current++;
    console.log(`[CMSLayout] Render #${renderCountRef.current} on path: ${location.pathname}`);
    
    return () => {
      const lifetime = (Date.now() - mountTimeRef.current) / 1000;
      console.log(`[CMSLayout] Unmounting after ${lifetime.toFixed(2)}s and ${renderCountRef.current} renders from path: ${location.pathname}`);
    };
  }, []); // Empty dependency array - no re-initialization
  
  // Create navItems as a stable reference
  const navItems: NavItemType[] = [
    {
      to: '/cms',
      icon: <LayoutDashboard size={18} />,
      label: 'Dashboard',
    },
    {
      to: '/cms/exhibits',
      icon: <FileText size={18} />,
      label: 'Exhibits',
    },
    {
      to: '/cms/tours',
      icon: <Map size={18} />,
      label: 'Tours',
    },
    {
      to: '/cms/media',
      icon: <Image size={18} />,
      label: 'Media Library',
    },
    {
      to: '/cms/visitor-info',
      icon: <Info size={18} />,
      label: 'Visitor Info',
    },
    {
      to: '/cms/settings',
      icon: <Settings size={18} />,
      label: 'Settings',
    },
    {
      to: '/cms/users',
      icon: <Users size={18} />,
      label: 'Users',
    },
  ];

  const isActive = (path: string) => {
    const currentPath = location.pathname;
    if (path === '/cms') {
      return currentPath === '/cms';
    }
    return currentPath.startsWith(path);
  };

  const handleSignOut = async () => {
    console.log('[CMSLayout] Sign out requested');
    // Will be implemented in Phase 6
  };

  console.log(`[CMSLayout] Rendering layout for path: ${location.pathname}`);

  // Direct rendering without any conditional logic that could trigger redirects
  return (
    <div 
      className="flex min-h-screen" 
      data-testid="cms-layout"
      data-path={location.pathname}
    >
      {/* Desktop Sidebar */}
      <DesktopSidebar 
        navItems={navItems}
        isActive={isActive}
        onSignOut={handleSignOut}
      />

      {/* Mobile Sidebar */}
      <MobileSidebar
        navItems={navItems}
        isActive={isActive}
        onSignOut={handleSignOut}
        open={open}
        setOpen={setOpen}
      />

      {/* Main Content - Direct outlet rendering without any conditional logic */}
      <main className="flex-1 overflow-auto w-full max-w-full">
        <div className="container mx-auto p-4 md:p-6 w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
